import React, { useState, FormEvent, KeyboardEvent, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { logUiEvent } from '@/utils/logger';
import MessageTextarea from './MessageTextarea';
import InputToolbar from './InputToolbar';

interface CopilotInputBarProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
  minRows?: number;
  maxRows?: number;
  value?: string;
  onChange?: (value: string) => void;
  onClose?: () => void;
}

export const CopilotInputBar: React.FC<CopilotInputBarProps> = ({
  onSendMessage,
  isLoading = false,
  placeholder = 'Message Copilot...',
  disabled = false,
  minRows = 1,
  maxRows = 9,
  value: controlledValue,
  onChange: controlledOnChange,
  onClose
}) => {
  const [internalMessage, setInternalMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hasPendingMessage, setHasPendingMessage] = useState(false);
  const [isReady, setIsReady] = useState(false);
  
  // ✅ READY STATE: Emit ready event when input is fully initialized
  useEffect(() => {
    // Mark as ready after initial render and controlled value sync
    const readyTimer = setTimeout(() => {
      setIsReady(true);
      // Emit custom event to notify external components that input is ready
      window.dispatchEvent(new CustomEvent('copilot-input-ready', {
        detail: { timestamp: Date.now() }
      }));
    }, 100); // Small delay to ensure DOM is ready
    
    return () => clearTimeout(readyTimer);
  }, []); // Only run once on mount
  
  // ✅ SIMPLIFIED: Use internal state as primary source during typing
  // Only sync with controlled value when it changes externally (e.g. prompt application)
  useEffect(() => {
    if (controlledValue !== undefined && controlledValue !== internalMessage) {
      // Only update internal state if controlled value changed externally
      // This prevents localStorage overwrites during typing
      setInternalMessage(controlledValue);
      
      // ✅ READY STATE: Emit value-updated event when external value is applied
      if (isReady && controlledValue !== '') {
        window.dispatchEvent(new CustomEvent('copilot-input-value-updated', {
          detail: { value: controlledValue, timestamp: Date.now() }
        }));
      }
    }
  }, [controlledValue, isReady]);
  
  // ✅ RESPONSIVE: Always use internal state for immediate display
  const displayMessage = internalMessage;
  
  // ✅ FIXED: Call parent onChange to enable localStorage synchronization
  const handleLocalChange = useCallback((value: string) => {
    setInternalMessage(value);
    // ✅ CRITICAL: Call parent onChange to sync with localStorage
    if (controlledOnChange) {
      controlledOnChange(value);
    }
  }, [controlledOnChange]);
  
  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    
    const trimmedMessage = displayMessage.trim();
    if (!trimmedMessage || trimmedMessage.length === 0 || isLoading || disabled) return;
    
    // Flag for immediate UI feedback
    setHasPendingMessage(true);
    setTimeout(() => setHasPendingMessage(false), 500);
    
    logUiEvent('CopilotInputBar', 'message submitted', {
      messageLength: trimmedMessage.length
    });
    
    onSendMessage(trimmedMessage);
    
    // ✅ CLEAN RESET: Clear internal state and notify parent
    setInternalMessage('');
    if (controlledOnChange) {
      controlledOnChange('');
    }
  };
  
  // ✅ PROGRAMMATIC SEND: Listen for external trigger to send message (moved after handleSubmit)
  useEffect(() => {
    const handleProgrammaticSend = () => {
      console.log('🎯 Programmatic send triggered');
      handleSubmit();
    };
    
    window.addEventListener('copilot-trigger-send', handleProgrammaticSend);
    
    return () => {
      window.removeEventListener('copilot-trigger-send', handleProgrammaticSend);
    };
  }, [handleSubmit]); // Include handleSubmit in dependencies
  
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter without Shift key
    if (e.key === 'Enter' && !e.shiftKey) {
      logUiEvent('CopilotInputBar', 'enter key pressed');
      e.preventDefault();
      handleSubmit();
    }
  };
  
  return (
    <motion.div
      className="relative overflow-hidden backdrop-blur-xl backdrop-saturate-150 transition-all duration-300 px-4 rounded-xl"
      initial={{ y: 10, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      data-testid="copilot-input-bar"
      data-input-ready={isReady}
    >
      {/* Gradient background - darker, more background-like */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50/30 via-slate-100/20 to-slate-50/10 dark:from-slate-900/60 dark:to-slate-800/40 rounded-xl -z-10" />
      
      {/* Top border highlight */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200/30 to-transparent dark:via-slate-700/30 rounded-t-xl" />
      <div className="flex flex-col py-2.5">
        {/* Message textarea - matches message bubble padding */}
        <div className="bg-blue-100/60 dark:bg-blue-900/40 border border-blue-300/60 dark:border-blue-700/60 rounded-2xl px-4 py-2.5">
          <MessageTextarea
            value={displayMessage}
            onChange={handleLocalChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            minRows={minRows}
            maxRows={maxRows}
          />
        </div>
        
        {/* Spacing between textarea and toolbar */}
        <div className="h-2.5" />
        
        {/* Input toolbar with buttons */}
        <form onSubmit={handleSubmit}>
          <InputToolbar
            isFocused={isFocused}
            hasPendingMessage={hasPendingMessage}
            isLoading={isLoading}
            disabled={disabled}
            message={displayMessage}
            onClose={onClose}
          />
        </form>
      </div>
    </motion.div>
  );
};

export default CopilotInputBar;
